import { type FC, useRef, useState } from 'react';
import {
  Button,
  InlineNotification,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { SidePanel } from '@carbon/ibm-products';
import { useAtom } from 'jotai';
import {
  EntityForm,
  type EntityFormHandle,
  type EntitySeedValues,
  useEntityFormServerErrors,
} from 'components/entity-form';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import type { DocumentResponse } from 'types/api-responses';
import type { EntitySearchResult } from 'pages/entities/list/types';
import {
  CLIENT_SESSION_ID,
  partyUrl,
  saveUserSelectionAtom,
} from './atoms/structured-parties';
import { EntityTablePicker } from './entity-table-picker';
import {
  StagedEntityEditor,
  type StagedEntityEditorHandle,
} from './staged-entity-editor';
import { MoveAddressModal } from './move-address-modal';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import { SharedAnchorNotice } from './shared-anchor-notice';
import { SharedRenameModal } from './shared-rename-confirm';
import { useMoveAddress, useStagedEditsSave } from './hooks';
import {
  anchorCard,
  anchorName as anchorNameFor,
  anchorSourceLabel,
} from './utils';
import 'components/inline-edit';
import type { StructuredPartyDetail, StructuredPartyListItem } from './types';
import style from './change-match-panel.module.scss';

type Mode = 'select' | 'create';

interface Props {
  open: boolean;
  document: DocumentResponse;
  party: StructuredPartyDetail;
  allParties?: StructuredPartyListItem[];
  onClose: () => void;
  onSaved: () => void;
  onMutated: () => void;
}

/**
 * The Create New Entity form's seed. The selection is recorded against the
 * party's current anchor, so a new entity starts from it.
 *
 * @param {StructuredPartyDetail} party - The party being matched.
 * @return {EntitySeedValues} The form's starting values.
 */
const createSeedFor = (party: StructuredPartyDetail): EntitySeedValues => {
  const address = anchorCard(party)?.primaryAddress;
  return {
    name: anchorNameFor(party) || null,
    address: address
      ? {
          streetAddress: address.streetAddress,
          city: address.city,
          state: address.state,
          zipCode: address.zipCode,
        }
      : undefined,
  };
};

export const ChangeMatchPanel: FC<Props> = ({
  open,
  document,
  party,
  allParties = [],
  onClose,
  onSaved,
  onMutated,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [mode, setMode] = useState<Mode>('select');
  const [selectedEntity, setSelectedEntity] =
    useState<EntitySearchResult | null>(null);
  const {
    error,
    setError,
    fieldErrors,
    clearFieldErrors,
    clearErrors,
    showServerError,
  } = useEntityFormServerErrors();
  const createFormRef = useRef<EntityFormHandle>(null);
  const stagedEditorRef = useRef<StagedEntityEditorHandle>(null);
  const [createFormValid, setCreateFormValid] = useState(false);
  const edits = useStagedEditsSave({
    documentId: document.id,
    party,
    failure: 'Could not update party details',
    onStart: clearErrors,
    onError: setError,
  });
  const [matchState, triggerMatch] = useAtom(saveUserSelectionAtom);
  const pending = edits.saving || matchState.loading;
  const pageContentSelector = useExclusiveDrawer('change-match', open, onClose);

  // Start over each time the panel opens, and when its party changes.
  const sessionKey = open ? `${party.kind}-${party.id}` : null;
  const [seededFor, setSeededFor] = useState(sessionKey);
  // The anchor the examiner is matching against: the one the panel opened on,
  // then the one Create New Entity was last seeded from. A match saved after a
  // refetch moved it is refused rather than recorded against the new one.
  const [seededAnchorId, setSeededAnchorId] = useState(party.currentAnchor?.id);
  if (seededFor !== sessionKey) {
    setSeededFor(sessionKey);
    setSeededAnchorId(party.currentAnchor?.id);
    setIsEditing(false);
    setMode('select');
    setSelectedEntity(null);
    clearErrors();
    setCreateFormValid(false);
  }

  const moveAddress = useMoveAddress({
    document,
    allParties,
    party,
    onMoved: onMutated,
  });

  const anchor = anchorCard(party);
  const anchorLabel = anchorSourceLabel(party.currentAnchor?.source);

  // Seeded each time Create New Entity is chosen (and per party/anchor) so a
  // refetch while it is open doesn't reset what the examiner typed.
  const createSeedKey =
    open && mode === 'create'
      ? `${party.kind}-${party.id}-${anchor?.id}`
      : null;
  const [createSeed, setCreateSeed] = useState(() => ({
    key: createSeedKey,
    values: createSeedFor(party),
  }));
  if (createSeed.key !== createSeedKey) {
    setCreateSeed({ key: createSeedKey, values: createSeedFor(party) });
    if (createSeedKey) setSeededAnchorId(party.currentAnchor?.id);
  }

  // A new entity travels with the selection, so a refused anchor creates none.
  const persistMatch = (
    selection:
      { suggested_entity_id: number } | { entity: Record<string, unknown> }
  ) => {
    clearErrors();
    triggerMatch({
      url: partyUrl(document.id, party.id, party.kind, '/match_candidate'),
      type: 'POST',
      data: {
        ...selection,
        client_session_id: CLIENT_SESSION_ID,
        anchor_entity_id: seededAnchorId,
      },
      onSuccess: () => onSaved(),
      onError: (err) => showServerError(err, 'Could not save match candidate'),
    });
  };

  const handlePanelSave = () => {
    if (!selectedEntity) return;
    persistMatch({ suggested_entity_id: selectedEntity.id });
  };

  const handleDetailsUpdate = () =>
    edits.save(stagedEditorRef.current, {
      onUnchanged: () => setIsEditing(false),
      onSaved: () => {
        setIsEditing(false);
        onMutated();
      },
    });

  const isSelectMode = mode === 'select';
  const selectSaveDisabled = pending || isEditing || !selectedEntity;
  const createSaveDisabled = pending || isEditing || !createFormValid;

  const actions = [
    {
      kind: 'secondary' as const,
      label: 'Cancel',
      onClick: onClose,
      disabled: pending,
    },
    {
      kind: 'primary' as const,
      label: 'Save',
      onClick: isSelectMode
        ? handlePanelSave
        : () => createFormRef.current?.submit(),
      disabled: isSelectMode ? selectSaveDisabled : createSaveDisabled,
    },
  ];

  const renderStagedSection = () => (
    <>
      <StagedEntityEditor
        ref={stagedEditorRef}
        party={party}
        disabled={pending || !isEditing}
        onMoveAddress={isEditing ? () => moveAddress.open() : undefined}
      />
      {isEditing && (
        <div className={style.editActions}>
          <Button
            kind="primary"
            size="sm"
            disabled={pending}
            onClick={handleDetailsUpdate}
          >
            Update
          </Button>
          <Button
            kind="tertiary"
            size="sm"
            disabled={pending}
            onClick={() => {
              stagedEditorRef.current?.reset();
              setIsEditing(false);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </>
  );

  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".change-match-sidepanel"
        pageContentSelector={pageContentSelector}
        defaultWidthPx={640}
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Change Match"
        subtitle="Edit the party's extracted details, or pick a different entity match."
        className={`change-match-sidepanel ${style.sidePanel}`}
        slideIn
        selectorPageContent={pageContentSelector}
        placement="right"
        size="lg"
        actions={actions}
      >
        <div className={style.body}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}

          <SharedAnchorNotice sharedWith={party.sharedWith} />

          <section className={style.section}>
            <div className={style.sectionTitle}>
              {anchorLabel} Details
              {!isEditing && (
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  tooltipPosition="bottom"
                  tooltipAlignment="end"
                  renderIcon={Edit}
                  iconDescription="Edit"
                  className={style.editBtn}
                  onClick={() => setIsEditing(true)}
                  disabled={pending}
                />
              )}
            </div>
            {renderStagedSection()}
          </section>

          <section className={style.matchSection}>
            <div className={style.sectionHeader}>
              <h4 className={style.matchTitle}>Resolve Entity Match</h4>
            </div>

            <RadioButtonGroup
              name="change-match-mode"
              legendText=""
              orientation="horizontal"
              valueSelected={mode}
              onChange={(value) => {
                setMode(value as Mode);
                setSelectedEntity(null);
              }}
              className={style.pathRadio}
            >
              <RadioButton
                id="change-match-mode-select"
                labelText="Select from Entities"
                value="select"
              />
              <RadioButton
                id="change-match-mode-create"
                labelText="Create New Entity"
                value="create"
              />
            </RadioButtonGroup>

            {isSelectMode ? (
              <EntityTablePicker
                selectedEntity={selectedEntity}
                onSelect={setSelectedEntity}
              />
            ) : (
              <div className={style.createWrap}>
                <EntityForm
                  ref={createFormRef}
                  entity={undefined}
                  getEntity={undefined}
                  loading={false}
                  onSubmitPayload={(entity) => persistMatch({ entity })}
                  fieldErrors={fieldErrors}
                  onFieldErrorsStale={clearFieldErrors}
                  showCancel={false}
                  seedValues={createSeed.values}
                  hideSubmitButton
                  onValidityChange={setCreateFormValid}
                />
              </div>
            )}
          </section>
        </div>
      </SidePanel>

      <MoveAddressModal {...moveAddress.modalProps} />

      <SharedRenameModal {...edits.renameModalProps} />
    </>
  );
};
